import type { ReactNode } from 'react';

// UX-35 — extrait de `StudentsOverview.tsx` (UX-18, seule implémentation de ce
// calcul avant ce ticket) vers ce fichier partagé : `ManageClasses.tsx` en
// devient le SECOND consommateur réel (tableau des classes, colonne
// "Remplissage" avec barre colorée par seuil) — même précédent que
// `computeEnrolledStudentsSummary()` (UX-06), jamais une seconde
// implémentation divergente des mêmes seuils.
//
// Seuils DOCUMENTÉS EXPLICITEMENT, comme demandé par UX-18 (INCHANGÉS par ce
// ticket) : `capacity === null` (classe SANS capacité configurée) → traitée
// comme ILLIMITÉE, même philosophie que partout ailleurs dans ce produit
// (ENR-06 : "classe sans capacité configurée -> illimitée, jamais un
// blocage") — "Places dispo.", places restantes affichées "—" (jamais un
// chiffre fabriqué). `capacity` défini : `remaining = max(capacity -
// studentCount, 0)` — `0` → "Complète" ; `1` ou `2` → "Presque pleine"
// (seuil confirmé par les exemples chiffrés de la maquette "Admin
// Dashboard.dc.html" : PS A, 1 place restante → "Presque pleine" ; CP A, 3
// places → "Places dispo.") ; `>= 3` → "Places dispo.".
export type ClassCapacityTone = 'amber' | 'brand' | 'gray';

export interface ClassCapacityStatus {
  label: string;
  tone: ClassCapacityTone;
  remainingLabel: string;
}

export function classCapacityStatus(capacity: number | null, studentCount: number): ClassCapacityStatus {
  if (capacity === null) {
    return { label: 'Places dispo.', tone: 'brand', remainingLabel: '—' };
  }
  const remaining = Math.max(capacity - studentCount, 0);
  if (remaining === 0) return { label: 'Complète', tone: 'gray', remainingLabel: '0' };
  if (remaining <= 2) return { label: 'Presque pleine', tone: 'amber', remainingLabel: String(remaining) };
  return { label: 'Places dispo.', tone: 'brand', remainingLabel: String(remaining) };
}

const BADGE_TONE_CLASSNAMES: Record<ClassCapacityTone, string> = {
  amber: 'bg-amber-100 text-amber-800',
  brand: 'bg-brand-100 text-brand-700',
  gray: 'bg-gray-100 text-gray-600',
};

// GÉNÉRIQUE (pas seulement la capacité de classe) — reprend TEL QUEL l'ancien
// `statusBadge()` local de `StudentsOverview.tsx` (déjà réutilisé là-bas pour
// un badge "En attente" sans rapport avec la capacité) : nommé `toneBadge`
// plutôt que `classCapacityBadge`, honnête sur sa portée réelle.
export function toneBadge(label: string, tone: ClassCapacityTone): ReactNode {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${BADGE_TONE_CLASSNAMES[tone]}`}>{label}</span>
  );
}

// UX-35 — même palette que `classCapacityBadge` ci-dessus (fond du badge →
// couleur du remplissage), pour une barre de remplissage colorée SELON LE
// TAUX (nouveau, `ManageClasses.tsx`) — jamais une seconde palette inventée.
// UX-64 : `amber-600` (accent Figma, #D97706) — remplace `amber-500`, jamais de
// texte directement sur cette barre, aucune contrainte de contraste texte
// applicable (voir docs/history/UX.md "UX-64").
const FILL_TONE_CLASSNAMES: Record<ClassCapacityTone, string> = {
  amber: 'bg-amber-600',
  brand: 'bg-brand-500',
  gray: 'bg-gray-400',
};

export function classCapacityFillClassName(tone: ClassCapacityTone): string {
  return FILL_TONE_CLASSNAMES[tone];
}
